
import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from 'layout/Table/Table';
import { useDrivingRangeLists } from 'hooks/RangeDetails';
import { Skeleton, Tooltip } from '@mui/material';
import { decryptSecretKey, dateTimeFormat } from 'services/utils';
import { clearDrivingRangeList, clearsearchRangeName, drivingRange_list, searchRangeName } from 'redux/DrivingRangeSlice';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import Wrapper from "layout/Wrapper/Wrapper";
import asset from 'json/assets';
import DrivingRangeSkeleton from 'ui/Skeletons/DrivingRangeSkeleton';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import TableSkeletonRows from 'ui/Skeletons/TableSkeletonRows';

import styles from 'styles/pages/DrivingRange.module.scss';


const DrivingRange = () => {
  const drivingRangeLists = useDrivingRangeLists()
  const [idRange, setIdRange] = useState("");
  const [idRangeInt, setIdRangeInt] = useState();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [rangeNameFordrivingRangeDetails, setrangeNameForDrivingRangeDetails] = useState(drivingRangeLists?.rangeName)
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const containerRef = useRef(null);
  const secret_Key = decryptSecretKey();
  const profileDetails = JSON.parse(localStorage.getItem("profile_data"));
  const dispatch = useDispatch();  
  const navigate=useNavigate();
  const redirect=()=>{
    navigate('/rangeinformation')
  }
  const redirectToMap=(e,rangeId)=>{
    navigate(`/mapview/rangemap?range=${rangeId}`)
  }  
  
  useEffect(() => {      
    if (drivingRangeLists?.driving_range_data.rangeList?.length > 0) {
      if (page === 1) {        
        setDrivers(drivingRangeLists.driving_range_data.rangeList);
      } else {
        setDrivers(prev => [...prev, ...drivingRangeLists.driving_range_data.rangeList]);
      }
      
      setHasMoreItems(
        drivingRangeLists.driving_range_data.rangeList.length <
          (drivingRangeLists.driving_range_data.totalRanges || 0)
      );
    }
    }, [drivingRangeLists.driving_range_data, page]); 
    
  useEffect(() => {
    setDrivers([]);
    setPage(1);
    setHasMoreItems(true);    
  }, [drivingRangeLists.rangeName]);
  
  const toEditRangeInfoPage = (e,rangeId,rangeIdInt)=>{
    navigate(`/editRangeInformation/${rangeIdInt}?range=${rangeId}`)
    // for edit page need id of range to pass
    setIdRange(rangeId);
    setIdRangeInt(rangeIdInt);
  }
  
  const sortedDrivingRangeList = React.useMemo(() => {
    if(drivers.length) {
        let sortableItems = [...drivers];

        if (sortConfig.key) {
          sortableItems.sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) {
              return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
              return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
          });
      }
      return sortableItems;
    }
    return [];
  }, [drivers, sortConfig]);  
  
  const requestSort = (key) => {
    
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const fetchDrivingRange = useCallback((page = 1, reset = false) => {
    const trimmedName = drivingRangeLists?.rangeName?.trim();

    if (!trimmedName) {
				return Promise.resolve();
			}
    
    if (reset) {
      setPage(1);
      dispatch(clearDrivingRangeList());
      setDrivers([]);
    } else {
      setPage(page);
    }
    
    // before starting the fetch, flip the loading flag
    setIsLoadingMore(true);
    
    dispatch(drivingRange_list({
      body: {
        active: 1,
        rangeName: trimmedName,
        countryFormat:4,
        stateFormat:4,
        page: page,
        resultsPerPage: 100
      },
      user: {
        secretKey: secret_Key,
        userName:  profileDetails?.username,
      },
      }))
      .then((resp) => {
        const fetchedList = resp?.payload?.data?.rangeList || [];
        const totalRanges = resp?.payload?.data?.totalRanges || 0;
        
        setDrivers((prev) => {
          const updated = reset ? fetchedList : [...prev, ...fetchedList];
          const maxPageReached = page >= 5;
          const moreItemsAvailable = updated.length < totalRanges;
          setHasMoreItems(!maxPageReached && moreItemsAvailable);
          
          return updated;
        })
			})
      .finally(() => {
        setIsLoadingMore(false);
      });
  }, [dispatch, drivingRangeLists?.rangeName, profileDetails?.username, secret_Key])

  const handleScroll = useCallback(() => {      
      if (!containerRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (
        scrollHeight - scrollTop - clientHeight < 100 &&
        hasMoreItems &&
        !isLoadingMore &&
        page < 5
      ) {
        const next = page + 1;
        setPage(next);
        fetchDrivingRange(next);
      }
    }, [page, hasMoreItems, isLoadingMore, fetchDrivingRange]);
  
    // now we can safely debounce that callback
    const debouncedHandleScroll = useMemo(
      () => debounce(handleScroll, 200),
      [handleScroll]
    );
  
    useEffect(() => {
      return () => {
        debouncedHandleScroll.cancel();
      };
    }, [debouncedHandleScroll]);
    
  return (
    <div className="main_wraper">
      <Wrapper isHeader={true} headerTitle={'DRIVING RANGE'}>
          <div className={styles.range_sec}>
            <Table title="Ranges" addIcon={true} addIconOnClick={redirect}>
              {/* <table className={styles.table_bar}> */}
              {drivingRangeLists?.driving_range_status === "loading" && drivers.length === 0 
              ? (<DrivingRangeSkeleton/>): (
                <>
                  {drivers.length > 0 ? (
                    <div className={styles.new_table} ref={containerRef} onScroll={debouncedHandleScroll}>
                      <table className={styles.table_bar}>
                      <thead>
                        <tr>
                        <th className={styles.name} >ID</th>
                          <th className={styles.name} onClick={() => requestSort('rangeName')}>NAME</th>
                          <th className={styles.city} onClick={() => requestSort('city')}>CITY</th>
                          <th className={styles.state} onClick={() => requestSort('stateFull')}>STATE</th>
                          <th className={styles.country} onClick={() => requestSort('countryFull')}>COUNTRY</th>
                          <th className={styles.modyfied} onClick={() => requestSort('rangeModified')}>LAST MODIFIED</th>
                          <th className={styles.action}>ACTION</th>
                        </tr>
                      </thead>
                      <tbody>
                      {/* {drivingRangeLists?.driving_range_data?.rangeList?.map((range)=>{ */}
                      {sortedDrivingRangeList?.map((range)=>{
                        return (
                          <tr 
                              //  className={range?.id >= 10 ? `${styles.active_box}`: null } 
                                key={range?.id_drivingRange} id={range?.id_drivingRangeInt}>
                                <td>{range?.id_drivingRangeInt}</td>
                                <td className={styles.name_cont}>
                                  {range?.rangeName}
                                  {/* Driving Range Name 01 */}
                                  <div className={styles.city_box}>
                                    {/* <p>City Name, CA</p> */}
                                    <p>{range?.city}, {range?.stateShort}</p>
                                  </div>
                                </td>
                                <td className={styles.city_cont}>{range?.city}</td>
                                {range?.stateShort && range?.stateFull ? (<td className={styles.state_cont}>{range?.stateShort}</td>):(<td className={styles.state_cont}>--</td>)}
                                <td className={styles.country_cont}>
                                  {/* United States of America */}
                                  {range?.countryFull}
                                </td>
                                <td 
                                className={styles.modified_cont}
                                >{dateTimeFormat(range?.rangeModified)}</td>
                                <td className={styles.action_cont}>
                                  <div className={styles.action_bar}>
                                  <Tooltip title="Mapping" arrow placement="top">
                                  <button><img src={asset.view} alt="" onClick={(event) =>redirectToMap(event, range?.id_drivingRange,range?.id_drivingRangeIn)}/></button>
                                  </Tooltip>
                                  <Tooltip title="Info" arrow placement="top">
                                  <button><img src={asset.pencilicon} alt="" 
                                          onClick={(event) =>toEditRangeInfoPage(event, range?.id_drivingRange,range?.id_drivingRangeInt)}
                                    /></button>
                                  </Tooltip>
                                  </div>
                                </td>
                              </tr>
                        )
                      })}
                      {hasMoreItems && (
                        <TableSkeletonRows rows={3} columns={7} />
                      )}
                  </tbody>
                      </table>
                    </div>
                  ): drivingRangeLists?.rangeName && drivingRangeLists?.driving_range_data.rangeList?.length === 0 ? (
                    <div className={styles.searchDrivingRange}>
                      <SearchOffIcon className={styles.notDataicon} />
                      <p className={styles.notDataContent}>No Driving Range Found</p>
                      <p className={styles.dataSearchContent}>Please refine your search request</p>
                    </div>
                  ) : (
                    <div className={styles.searchDrivingRange}>
                      <SearchIcon className={styles.notDataicon} />
                    <p className={styles.notDataContent}>Please Search For Driving Range</p>
                    </div>
                  )}
                </>
              )}
              {/* </table> */}
            </Table>
          </div>
      </Wrapper>
    </div>
  )
}


export default DrivingRange;